import { When } from 'react-if';
import { type SupportedCountryCode } from '@zep-us/i18n';
import { Button, ICON_COLOR } from '@zep-us/zus';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { type CategoryTreeBuildDto } from '@/api';
import { useViewport } from '@/hooks';
import { CheckSmallIcon } from '@/icons';
import { type CategoryTree } from '@/shared/category/type';
import { CategoryTextDisplayView, useCategoryDisplayText } from '@/views/build/features/category-display-text';
import { type CategoryInfoInputType } from '@/views/build/features/category-info';
import { PublisherSelection } from '@/views/build/features/publisher-category-selector';
import { GradeSelection, SchoolTypeSelection, SemesterSelection } from '@/views/build/features/school-category-selector';
import { SubjectSelection } from '@/views/build/features/subject-category-selector';
import { type SelectionSchoolInfo } from '@/views/build/type';
import { useCategorySelector } from '../../category-selector/hooks/useCategorySelector';
import { convertSelectedCategoryInfoToFormValue } from '../utils';
import { CategoryInfoMobileView } from './CategoryInfoMobileView';
import { ColumnUI } from './ColumnUI';
import { SaveForNextQuizCheckbox } from './SaveForNextQuizCheckbox';
export type CategoryInfoTrigger = 'new' | 'edit';
export interface CategoryInfoViewProps {
  /** 폼의 초기값 (교육 정보 필드들) */
  formValues: CategoryInfoInputType;
  /** BuildInfo의 학교 정보 트리 */
  schoolInfoTree: CategoryTree;
  /** BuildInfo의 과목 트리 */
  subjectTree: CategoryTreeBuildDto[];
  /** BuildInfo의 출판사 정보 */
  publishers?: SelectionSchoolInfo['publishers'];
  /** 사용자 국가 코드 */
  userCountryCode: SupportedCountryCode;
  /** 폼 완료(제출) 시 호출되는 콜백 */
  onComplete: (values: CategoryInfoInputType) => void;
  /** 모달 트리거 유형 ('new': 신규 생성 플로우, 'edit': 설정에서 수정) */
  trigger?: CategoryInfoTrigger;
}

/**
 * CategoryInfoView - 완전히 독립적인 카테고리 정보 선택 뷰 컴포넌트
 *
 * BuildInfoProvider와 FormProvider에 의존하지 않고,
 * 필요한 데이터를 props로 받아 로컬 state로 관리합니다.
 * 어떤 환경에서든 재사용 가능한 완전히 독립적인 컴포넌트입니다.
 *
 * @param formValues - 외부에서 주입된 폼 데이터
 * @param schoolInfoTree - 학교 정보 트리 데이터
 * @param subjectTree - 과목 트리 데이터
 * @param publishers - 출판사 정보
 * @param userCountryCode - 사용자 국가 코드
 * @param onComplete - 유효한 데이터로 폼이 제출될 때 호출되는 콜백
 * @param trigger - 모달 트리거 유형 ('new': 신규 생성 플로우, 'edit': 설정에서 수정)
 */
const CategoryInfoView = ({
  formValues,
  schoolInfoTree,
  subjectTree,
  publishers,
  userCountryCode,
  onComplete,
  trigger
}: CategoryInfoViewProps) => {
  const {
    t
  } = useTranslation(['build']);
  const {
    isMobile,
    isTabletSm
  } = useViewport();
  const categoryInfo = useCategorySelector({
    formValues,
    schoolInfoTree,
    subjectTree,
    publishers: publishers?.[userCountryCode]
  });
  const displayText = useCategoryDisplayText({
    userCountryCode,
    categoryInfo,
    schoolInfoTree
  });
  const handleSubmit = () => {
    const {
      success
    } = categoryInfo.validate();
    if (success) {
      const formValue = convertSelectedCategoryInfoToFormValue(categoryInfo);
      if (!formValue) {
        //should not happen
        console.error('formValue is undefined');
        return;
      }

      // 카테고리 정보 입력 완료 이벤트 트래킹
      logger.track('category_input_complete', {
        trigger,
        save_for_next: categoryInfo.isLastSchoolInfoSaved
      });
      onComplete(formValue);
    }
  };

  // 모바일 뷰 렌더링
  if (isMobile || isTabletSm) {
    return <CategoryInfoMobileView categoryInfo={categoryInfo} onSubmit={handleSubmit} userCountryCode={userCountryCode} />;
  }

  // 데스크탑 뷰 렌더링
  return <div className="flex h-full min-h-0  flex-col px-lg" data-sentry-component="CategoryInfoView" data-sentry-source-file="CategoryInfoView.tsx">
      <CategoryTextDisplayView displayItems={displayText.displayTextItems} data-sentry-element="CategoryTextDisplayView" data-sentry-source-file="CategoryInfoView.tsx" />

      {/* 폼 콘텐츠 - 5열 그리드 레이아웃 */}
      <div className="-mx-3xs min-h-0 flex-1 overflow-hidden overflow-x-auto px-3xs pb-xs pt-lg">
        {/* 5열 캐스케이딩 선택 영역 */}
        <div className="flex h-full min-h-0 w-full min-w-[900px] gap-sm">
          {/* 학교급 */}
          <SchoolTypeSelection categoryInfo={categoryInfo} displayText={displayText} data-sentry-element="SchoolTypeSelection" data-sentry-source-file="CategoryInfoView.tsx" />
          {/* 구분선 */}
          <ColumnUI.Divider data-sentry-element="unknown" data-sentry-source-file="CategoryInfoView.tsx" />

          <GradeSelection categoryInfo={categoryInfo} displayText={displayText} data-sentry-element="GradeSelection" data-sentry-source-file="CategoryInfoView.tsx" />

          <When condition={userCountryCode === 'KR'} data-sentry-element="When" data-sentry-source-file="CategoryInfoView.tsx">
            <ColumnUI.Divider data-sentry-element="unknown" data-sentry-source-file="CategoryInfoView.tsx" />
            {/* 학기 */}
            <SemesterSelection categoryInfo={categoryInfo} displayText={displayText} data-sentry-element="SemesterSelection" data-sentry-source-file="CategoryInfoView.tsx" />
          </When>

          {/* 구분선 */}
          <ColumnUI.Divider data-sentry-element="unknown" data-sentry-source-file="CategoryInfoView.tsx" />

          {/* 과목 */}
          <SubjectSelection categoryInfo={categoryInfo} displayText={displayText} data-sentry-element="SubjectSelection" data-sentry-source-file="CategoryInfoView.tsx" />

          <When condition={userCountryCode === 'KR'} data-sentry-element="When" data-sentry-source-file="CategoryInfoView.tsx">
            {/* 구분선 */}
            <ColumnUI.Divider data-sentry-element="unknown" data-sentry-source-file="CategoryInfoView.tsx" />

            {/* 교과서 출판사 */}
            <PublisherSelection categoryInfo={categoryInfo} displayText={displayText} data-sentry-element="PublisherSelection" data-sentry-source-file="CategoryInfoView.tsx" />
          </When>
        </div>
      </div>

      {/* 하단 액션 버튼 */}
      <div className="flex flex-shrink-0 justify-between bg-default py-lg">
        {/* 체크박스 */}

        <SaveForNextQuizCheckbox checked={categoryInfo.isLastSchoolInfoSaved} onChange={checked => categoryInfo.setIsLastSchoolInfoSaved(checked)} data-sentry-element="SaveForNextQuizCheckbox" data-sentry-source-file="CategoryInfoView.tsx" />

        <Button variant="contained" intent="strong" size="lg" onClick={handleSubmit} leftSlot={<CheckSmallIcon size="md" fill={ICON_COLOR.inverse} />} style={{
        maxWidth: '392px'
      }} className="flex w-full" data-sentry-element="Button" data-sentry-source-file="CategoryInfoView.tsx">
          {t('build.category.schoolLevelSelectionComplete')}
        </Button>
      </div>
    </div>;
};
export { CategoryInfoView };